import React from 'react';
import PropTypes from 'prop-types';
import UserAvatar from 'treeline-react-core-components/UserAvatar';
import api from '../../Api';
import { connectToUser } from '../User/containers/UserDataStore';
import { connectToUserPreferences } from '../UserPreferences';
import { Modal, Popover, Input, Button, Tooltip } from 'antd';
import combineConnectors from '../util/combineConnectors';
import { Translate } from '@above-the-treeline/localization';
import TitleImage from '../Title/TitleImage';
import { connectToRequests } from '../Requests/containers/RequestsDataStore';
import ReviewCopyActionTooltip from './ReviewCopyActionTooltip';
import ProfileEditor from './ProfileEditor/ProfileEditor';
import {
	doOpenReviewCopyAction,
	deactivateAutoOpenReviewCopyAction,
} from './routines';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import { connectToAccountOrMembershipRequiredModalContext } from '../Anonymous/AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';
import './reviewCopy.less';

export class ReviewCopyRequestModalAnchor extends React.Component {
	static propTypes = {
		title: PropTypes.object,
		tooltipPlacement: PropTypes.string,
		digitalCreatedDate: PropTypes.string,
		digitalUpdatedDate: PropTypes.string,
		doAllowOpenOnMount: PropTypes.bool,
		isMobile: PropTypes.func,
	};

	static defaultProps = {
		doAllowOpenOnMount: false,
	};

	requestTypes = {
		drc: 1,
		prc: 2,
	};

	evaluationPurposeTypeIds = {
		acquisition: 1,
		advocating: 2,
		courseAdoption: 3,
		reviewing: 4,
	};

	state = {
		isModalOpen: false,
		isEditingProfile: false,
		selectedRequestType: this.requestTypes.drc,
		selectedTitleEvaluationPurposeTypeId: -1,
		selectedAddressId: -1,
		addressPopoverOpen: false,
		evaluationPurposePopoverOpen: false,
		department: '',
		course: '',
		enrollment: '',
		requestReason: '',
		sending: false,
		success: false,
		failure: false,
		message: '',
	};

	componentDidMount = () => {
		const {
			evaluationType,
		} = this.props.userPreferences.getReviewCopyDownloadPreferences();

		this.setState({
			selectedTitleEvaluationPurposeTypeId: evaluationType,
			selectedAddressId: this.getDefaultAddressId(),
		});

		if (this.props.doAllowOpenOnMount && doOpenReviewCopyAction()) {
			this.openModal();
			deactivateAutoOpenReviewCopyAction();
		}
	};

	handleClick = () => {
		const {
			userDataStore: { isAnonymous },
			accountOrMembershipRequiredModalContext: { setIsVisible },
		} = this.props;
		return isAnonymous() ? setIsVisible(true) : this.openModal();
	};

	render = () => (
		<div>
			{this.props.isMobile()
				? this.renderButton()
				: this.renderButtonWithTooltip()}
			<Modal
				wrapClassName={this.getWrapClassName()}
				bodyStyle={this.getModalBodyStyle()}
				title={this.getModalTitle()}
				width={700}
				visible={this.state.isModalOpen}
				onCancel={() => this.setState({ isModalOpen: false })}
				onOk={() => this.handleModalOnOk()}>
				{this.renderModalContents()}
			</Modal>
			<Modal
				visible={this.infoModalVisible()}
				closable={false}
				footer={[
					<Button
						type="primary"
						disabled={this.state.sending}
						onClick={() => {
							this.setState({
								sending: false,
								success: false,
								failure: false,
								message: '',
							});
						}}>
						<Translate id="ok" />
					</Button>,
				]}>
				{this.renderInfoModalContents()}
			</Modal>
		</div>
	);

	renderButton = () => {
		return (
			<div className="column-narrow">
				<Button shape="circle" onClick={this.handleClick}>
					<div className="icon-hand-paper-o request-hand" />
				</Button>
			</div>
		);
	};

	renderButtonWithTooltip = () => {
		return (
			<Tooltip
				title={this.getTooltip()}
				placement={this.props.tooltipPlacement || 'topRight'}>
				{this.renderButton()}
				<div className="clear" />
			</Tooltip>
		);
	};

	openModal = () => {
		this.setState({
			isModalOpen: true,
			isEditingProfile: false,
		});
	};

	getTooltip() {
		const { digitalCreatedDate, digitalUpdatedDate } = this.props;
		return (
			<ReviewCopyActionTooltip
				headerText={'Request DRC'}
				createdDate={digitalCreatedDate}
				updatedDate={digitalUpdatedDate}
			/>
		);
	}

	infoModalVisible = () =>
		this.state.sending || this.state.success || this.state.failure;

	renderInfoSendingModalContents = () => {
		return (
			<div>
				<Translate id="sending" />
			</div>
		);
	};

	renderInfoSuccessModalContents = () => {
		const title = this.getTitle();

		return (
			<div>
				<b className="success">
					<Translate id="success" />:
				</b>
				&nbsp;
				<span>
					<Translate id="request_sent_for" />
					&nbsp;<b>{title.name}</b>.
				</span>
			</div>
		);
	};

	renderInfoFailureModalContents = () => {
		const { message } = this.state;

		return (
			<div>
				<b className="error">
					<Translate id="error" />:
				</b>
				&nbsp;
				<span>{message}</span>
			</div>
		);
	};

	renderInfoModalContents = () => {
		const { sending, success, failure } = this.state;

		if (sending) {
			return this.renderInfoSendingModalContents();
		}

		if (success) {
			return this.renderInfoSuccessModalContents();
		}

		if (failure) {
			return this.renderInfoFailureModalContents();
		}

		return null;
	};

	getPopoverArrowClassName = visible =>
		`popover-arrow-icon ${
			visible ? 'icon-drop-up-icon-01' : 'icon-drop-down-icon'
		}`;

	handleModalOnOk = () => {
		this.setState({
			sending: true,
			success: false,
			failure: false,
		});

		if (this.isDrcSelected()) {
			this.submitDrcRequest();
		} else if (this.isPrcSelected()) {
			this.submitPrcRequest();
		}
	};

	submitDrcRequest = () => {
		const selectedTitleEvaluationPurposeTypeId = this.getSelectedTitleEvaluationPurposeTypeId();
		const requestProfile = encodeURIComponent(
			this.getUserOverview() || 'No Overview',
		);
		const requestReason = encodeURIComponent(this.getRequestReason());
		const department = encodeURIComponent(this.getDepartment());
		const course = encodeURIComponent(this.getCourse());
		const enrollment = encodeURIComponent(this.getEnrollment());
		const sku = this.getTitleSku();

		const params = {
			sku,
			selectedTitleEvaluationPurposeTypeId,
			requestProfile,
			department,
			course,
			enrollment,
			requestReason,
		};

		api.post('/api/v1/me/galleys/requests', params).then(response => {
			const { code, text } = response;
			const success = code === 'SUCCESS';
			const failure = !success;

			if (success) {
				this.props.requestsDataStore.refresh();
			}

			this.setState({
				sending: false,
				success,
				failure,
				message: text,
			});
		});
	};

	getPrcCourseAdoptionAttributes = () => {
		const department = this.getDepartment();
		const course = this.getCourse();
		const enrollment = this.getEnrollment();
		const attributes = [];

		if (department) {
			attributes.push({
				type: 1,
				value: encodeURIComponent(department),
			});
		}

		if (course) {
			attributes.push({
				type: 2,
				value: encodeURIComponent(course),
			});
		}

		if (enrollment) {
			attributes.push({
				type: 3,
				value: encodeURIComponent(enrollment),
			});
		}

		return attributes;
	};

	submitPrcRequest = () => {
		if (!this.isValidPrcRequest()) {
			this.setState({
				sending: false,
				success: false,
				failure: true,
				message: <Translate id="please_provide_shipping_for_request" />,
			});

			return;
		}

		const sku = this.getTitleSku();
		const phone = this.getPhoneNumber();
		const selectedAddress = this.getSelectedAddress();
		const selectedTitleEvaluationPurposeTypeId = this.getSelectedTitleEvaluationPurposeTypeId();
		const requestReason = this.getRequestReason();
		const emailAddress = this.getUserEmailAddress();
		const sourceUserId = this.getUserId();

		let lineItem = {
			sku,
			type: 0,
			requestedQty: 1,
			requestType: 10,
			emailAddress,
			sourceUserId,
			requestMessage: encodeURIComponent(requestReason),
			evaluationType: selectedTitleEvaluationPurposeTypeId,
			address: selectedAddress,
			phoneNumber: phone,
		};

		if (this.isCourseAdoptionSelected()) {
			const attributes = this.getPrcCourseAdoptionAttributes();

			if (attributes && attributes.length > 0) {
				lineItem.attributes = attributes;
			}
		}

		api.post('api/products/requests/0', lineItem).then(ret => {
			this.setState({
				sending: false,
				success: true,
				failure: false,
			});
		});
	};

	isValidPrcRequest = () => {
		const address = this.getSelectedAddress();
		const phone = this.getPhoneNumber();

		return address && phone && phone.length !== 0;
	};

	saveTitleEvaluationPurpose = selectedTitleEvaluationPurposeTypeId => {
		const preferenceType = 'DARC';
		const preferenceName = 'evaluationType';

		api.put(
			`/api/v1/me/preferences/${preferenceType}/${preferenceName}`,
			selectedTitleEvaluationPurposeTypeId,
		);
	};

	handleTitleEvaluationPurposeClick = selectedTitleEvaluationPurposeTypeId => {
		this.saveTitleEvaluationPurpose(selectedTitleEvaluationPurposeTypeId);

		this.setState({
			selectedTitleEvaluationPurposeTypeId,
			evaluationPurposePopoverOpen: false,
		});
	};

	handleShipToAddressClick = selectedAddressId =>
		this.setState({
			selectedAddressId,
			addressPopoverOpen: false,
		});

	getModalBodyStyle = () => {
		return { paddingTop: 10 };
	};

	getWrapClassName = () => {
		return this.state.isEditingProfile
			? 'review-copy-request-modal-editing-profile'
			: null;
	};

	getModalTitle = () => (
		<div className="review-copy-request-modal-title">
			{this.state.isEditingProfile
				? this.renderReviewCopyProfileEditorTitle()
				: this.renderReviewCopyRequestFormTitle()}
		</div>
	);

	renderReviewCopyRequestFormTitle = () => (
		<b>
			<Translate id="review_copy_request_form" />
		</b>
	);

	renderReviewCopyProfileEditorTitle = () => (
		<div className="profile-editor-title-bar">
			<div>
				<b>
					<Translate id="profile_editor" />
				</b>
				<span className="title-and-return-link-separator">•</span>
				<span
					onClick={this.returnToRequest}
					className="acc-font-no-hover">
					<span className="return-to-request-link">
						<Translate id="return_to_request" />
					</span>
					<span className="icon-book" />
				</span>
			</div>
			{this.renderUserProfileStrength()}
		</div>
	);

	renderModalContents = () => (
		<div className="review-copy-request-modal-contents">
			{this.state.isEditingProfile
				? this.renderReviewCopyProfileEditor()
				: this.renderReviewCopyRequestForm()}
		</div>
	);

	renderReviewCopyRequestForm = () => (
		<>
			{this.renderUserProfile()}
			<hr className="margin-top-5" />
			{this.renderTitleInfo()}
			{this.renderLibraryUpgradeInfo()}
		</>
	);

	renderReviewCopyProfileEditor = () => <ProfileEditor />;

	getTitle = () => this.props.title;

	getTitleUri = () => this.props.title.uri;

	getTitleSku = () => this.props.title.sku;

	getSelectedRequestType = () => this.state.selectedRequestType;

	isDrcSelected = () =>
		this.getSelectedRequestType() === this.requestTypes.drc;

	isPrcSelected = () =>
		this.getSelectedRequestType() === this.requestTypes.prc;

	isLibrary = () => this.props.userDataStore.state.organization.isLibrary;

	isAccountUpgradable = () =>
		this.props.userDataStore.state.user.user.isAccountUpgradable;

	getSelectedTitleEvaluationPurposeTypeId = () =>
		this.state.selectedTitleEvaluationPurposeTypeId;

	getDepartment = () => this.state.department;

	getCourse = () => this.state.course;

	getEnrollment = () => this.state.enrollment;

	getRequestReason = () => this.state.requestReason;

	getUserEmailAddress = () => this.props.userDataStore.state.user.user.email;

	getUserId = () => this.props.userDataStore.state.user.user.id;

	isCourseAdoptionSelected = () =>
		this.getSelectedTitleEvaluationPurposeTypeId() ===
		this.evaluationPurposeTypeIds.courseAdoption;

	getAddresses = () => {
		const { addresses } = this.props.userDataStore.state.user.user;

		return addresses;
	};

	getDefaultAddress = () => {
		const addresses = this.getAddresses();

		if (!addresses || addresses.length === 0) {
			return null;
		}

		const address = addresses.find(a => a.isDefault);

		if (address) {
			return address;
		}

		return addresses[0];
	};

	getDefaultAddressId = () => {
		const address = this.getDefaultAddress();

		if (address) {
			return address.addressId;
		}

		return -1;
	};

	getSelectedAddress = () => {
		const { selectedAddressId } = this.state;
		const addresses = this.getAddresses();

		if (!addresses || addresses.length === 0) {
			return null;
		}

		const address = addresses.find(a => a.addressId === selectedAddressId);

		if (address) {
			return address;
		}

		return this.getDefaultAddress();
	};

	getUserRole = () => {
		const {
			requestorType,
		} = this.props.userPreferences.getReviewCopyDownloadPreferences();

		return requestorType;
	};

	getUserOverview = () => {
		const {
			requestRole,
		} = this.props.userPreferences.getReviewCopyDownloadPreferences();

		return requestRole;
	};

	getUserAvatarUri = () => {
		const { userAvatar } = this.props.userDataStore.state;
		const { uri } = userAvatar;

		return uri;
	};

	getNumberOfJoinedCommunities = () => {
		const { joinedCommunities } = this.props.userDataStore.state;

		return (joinedCommunities && joinedCommunities.length) || 0;
	};

	getPhoneNumber = () => {
		const { phone } = this.props.userDataStore.state.user.user;

		return phone;
	};

	getAccentFontColor = () =>
		this.props.userPreferences.getFonts().accentFontColor;

	getSocialMediaLinksAndClassNames = () => {
		const userAttributes = this.props.userPreferences.getUserAttributes();
		const socialMediaIconClassByType = {
			'Personal Website Uri': 'icon-ios-body',
			'Twitter Uri': 'icon-twitter-icon',
			'Facebook Uri': 'icon-facebook-icon',
			'Goodreads Uri': 'icon-goodreads',
			'LinkedIn Uri': 'icon-linkedin-alt',
			'Skype Uri': 'icon-skype',
		};

		if (!userAttributes) {
			return [];
		}

		return Object.keys(userAttributes)
			.filter(type => type in socialMediaIconClassByType)
			.map(type => {
				return {
					type,
					href: userAttributes[type].value,
					className: socialMediaIconClassByType[type],
				};
			});
	};

	getUserProfileStrength = () => {
		const role = this.getUserRole();

		// Users who don't have a role defined are considered to have 'incomplete' profile strength.
		if (!role) {
			return -1;
		}

		const overview = this.getUserOverview();
		const avatarUri = this.getUserAvatarUri();
		const joinedCommunities = this.getNumberOfJoinedCommunities();
		const socialMediaLinks = this.getSocialMediaLinksAndClassNames();

		let strength = 0;

		// User overview is worth 2 points.
		if (overview) {
			strength += 2;
		}

		// Avatar image is worth 1 point.
		if (avatarUri) {
			strength++;
		}

		// Being in > 0 community is worth 1 point.
		if (joinedCommunities > 0) {
			strength++;
		}

		// Each social media link is worth 1 point.
		if (socialMediaLinks) {
			strength += socialMediaLinks.length;
		}

		return strength;
	};

	getUserProfileStrengthLocalizationAndColor = () => {
		const strength = this.getUserProfileStrength();

		if (strength === -1) {
			return { localization: 'incomplete', color: 'red' };
		}

		if (strength < 2) {
			return { localization: 'weak', color: 'blue' };
		}

		if (strength < 4) {
			return { localization: 'fair', color: 'blue' };
		}

		if (strength < 6) {
			return { localization: 'good', color: 'blue' };
		}

		return { localization: 'excellent', color: 'blue' };
	};

	renderUserOverview = () => {
		const overview = this.getUserOverview();

		if (overview) {
			return <span>{overview}</span>;
		}

		return (
			<i>
				<Translate id="no_overview" />
			</i>
		);
	};

	renderUserRole = () => {
		const role = this.getUserRole();

		if (role) {
			return <span>{role}</span>;
		}

		return (
			<i>
				<Translate id="no_role_specified" />
			</i>
		);
	};

	renderUserProfileStrengthHelpPopover = () => {
		const role = this.getUserRole();
		const avatarUri = this.getUserAvatarUri();
		const overview = this.getUserOverview();
		const numberOfJoinedCommunities = this.getNumberOfJoinedCommunities();
		const socialMediaLinks = this.getSocialMediaLinksAndClassNames();

		const getCheckmarkClassName = isChecked =>
			`icon-mark-icon ${isChecked ? 'green' : 'red'}`;

		return (
			<div className="user-profile-strength-popover-content">
				<span>
					<Translate id="profile_strength_description" />
				</span>
				<ul className="list-style-none pad-left-5">
					<li>
						<span className={getCheckmarkClassName(role)} />
						&nbsp;
						<span>
							<Translate id="role_summary" />
						</span>
					</li>
					<li>
						<span className={getCheckmarkClassName(avatarUri)} />
						&nbsp;
						<span>
							<Translate id="profile_picture" />
						</span>
					</li>
					<li>
						<span className={getCheckmarkClassName(overview)} />
						&nbsp;
						<span>
							<Translate id="description_of_role" />
						</span>
					</li>
					<li>
						<span
							className={getCheckmarkClassName(
								numberOfJoinedCommunities > 0,
							)}
						/>
						&nbsp;
						<span>
							<Translate id="affiliation" />
						</span>
					</li>
					<li>
						<span className="super-script-number">
							{socialMediaLinks.length}
						</span>
						&nbsp;
						<span>
							<Translate id="number_of_links" />
						</span>
					</li>
				</ul>
			</div>
		);
	};

	renderUserProfileStrength = () => {
		const {
			localization,
			color,
		} = this.getUserProfileStrengthLocalizationAndColor();

		return (
			<span className="user-profile-strength">
				<span className="strength-label-and-value">
					<span>
						<Translate id="profile_strength" />:
					</span>
					&nbsp;
					<span className={color}>{localization}</span>&nbsp;
				</span>
				<Popover
					placement="bottom"
					content={this.renderUserProfileStrengthHelpPopover()}
					trigger="hover">
					<span className="icon-help-icon clickable" />
				</Popover>
			</span>
		);
	};

	renderJoinedCommunitiesCount = () => {
		const joinedCommunities = this.getNumberOfJoinedCommunities();

		if (joinedCommunities === 0) {
			return null;
		}

		return (
			<span>
				{joinedCommunities} Affiliation
				{joinedCommunities > 1 ? 's' : ''}
			</span>
		);
	};

	renderSocialMediaLink = (href, className, idx) => {
		return (
			<span key={`sml-${idx}`}>
				<a
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					className="social-media-link">
					<span className={className}></span>
				</a>
			</span>
		);
	};

	renderSocialMediaLinks = () => {
		const socialMediaLinksAndClassNames = this.getSocialMediaLinksAndClassNames();

		return socialMediaLinksAndClassNames.map((i, idx) =>
			this.renderSocialMediaLink(i.href, i.className, idx),
		);
	};

	renderUserName = () => {
		const { userAvatar } = this.props.userDataStore.state;

		return <strong>{userAvatar.displayName}</strong>;
	};

	renderUserAvatar = () => {
		const { userAvatar } = this.props.userDataStore.state;

		return (
			<UserAvatar
				userInitials={userAvatar && userAvatar.initials}
				avatarUri={userAvatar && userAvatar.uri}
				displayName={userAvatar && userAvatar.displayName}
				avatarSizeClass="avatar-medium"
			/>
		);
	};

	renderUserProfile = () => {
		const profileEditIconClasses =
			'profile-edit-icon icon-note-icon acc-font-no-hover';

		return (
			<div>
				<div className="profile-header">
					<Translate id="your_saved_profile" />
					{this.renderUserProfileStrength()}
				</div>
				<div className="profile-overview">
					<div className="profile-image">
						{this.renderUserAvatar()}
					</div>
					<div className="profile-info">
						<div className="profile-info-first-row">
							<div className="first-row-item">
								{this.renderUserName()}
							</div>
							<div
								className="first-row-item"
								data-type="user-role">
								{this.renderUserRole()}
							</div>
							<span className="first-row-item-separator">•</span>
							<div className="first-row-item">
								{this.renderJoinedCommunitiesCount()}
							</div>
							<div
								className="first-row-item"
								data-type="social-media-links">
								{this.renderSocialMediaLinks()}
							</div>
						</div>
						<div className="profile-info-second-row">
							{this.renderUserOverview()}
						</div>
					</div>
					<div className="profile-edit-icon-container">
						<div
							onClick={this.showProfileEditor}
							className={profileEditIconClasses}
							title="Edit Profile"
						/>
					</div>
				</div>
			</div>
		);
	};

	showProfileEditor = () => {
		this.setState({ isEditingProfile: true });
	};

	returnToRequest = () => {
		this.setState({ isEditingProfile: false });
	};

	renderDigitalFormatOption = () => (
		<div>
			<input
				id="digital-request-format"
				type="radio"
				name="request-format"
				checked={this.isDrcSelected()}
				onChange={() =>
					this.setState({
						selectedRequestType: this.requestTypes.drc,
					})
				}
			/>
			<label htmlFor="digital-request-format">
				&nbsp;
				<Translate id="digital" />
			</label>
		</div>
	);

	renderPrintFormatOption = () => (
		<div>
			<input
				id="print-request-format"
				type="radio"
				name="request-format"
				checked={this.isPrcSelected()}
				onChange={() =>
					this.setState({
						selectedRequestType: this.requestTypes.prc,
					})
				}
			/>
			<label htmlFor="print-request-format">
				&nbsp;
				<Translate id="print" />
			</label>
		</div>
	);

	renderTitleFormats = () => {
		const title = this.getTitle();

		return (
			<div className="pad-top-5">
				<strong>
					<Translate id="format" />:
				</strong>
				{title.isRequestable && this.renderDigitalFormatOption()}
				{title.isPrcRequestable && this.renderPrintFormatOption()}
			</div>
		);
	};

	renderTitleJacketCover = () => (
		<TitleImage title={this.props.title} height={150} />
	);

	renderTitleName = () => {
		const title = this.getTitle();
		const accentFontColor = this.getAccentFontColor();

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="request_for" />
					:&nbsp;
				</strong>
				<b style={{ color: accentFontColor }}>{title.name}</b>
			</p>
		);
	};

	renderTitlePublisher = () => {
		const title = this.getTitle();

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="publisher" />
					:&nbsp;
				</strong>
				<span>{title.supplier}</span>
			</p>
		);
	};

	renderTitleEvaluationPurposePopoverRow = evaluationTypeId => {
		return (
			<tr
				className="title-evaluation-purpose-row clickable"
				onClick={() =>
					this.handleTitleEvaluationPurposeClick(evaluationTypeId)
				}>
				<td className="pad-5">
					<span className="icon-mark-icon" />
				</td>
				<td className="pad-5">
					<b>
						<Translate id={`evalType_${evaluationTypeId}`} />
					</b>
				</td>
				<td className="pad-5">
					<Translate id={`evalDesc_${evaluationTypeId}`} />
				</td>
			</tr>
		);
	};

	renderTitleEvaluationPurposePopover = () => {
		return (
			<div className="title-evaluation-purpose-popover-content">
				<span>
					<Translate id="select_best_option_for_request" />
				</span>
				<table>
					<tbody>
						{this.renderTitleEvaluationPurposePopoverRow(
							this.evaluationPurposeTypeIds.acquisition,
						)}
						{this.renderTitleEvaluationPurposePopoverRow(
							this.evaluationPurposeTypeIds.advocating,
						)}
						{this.renderTitleEvaluationPurposePopoverRow(
							this.evaluationPurposeTypeIds.courseAdoption,
						)}
						{this.renderTitleEvaluationPurposePopoverRow(
							this.evaluationPurposeTypeIds.reviewing,
						)}
					</tbody>
				</table>
			</div>
		);
	};

	renderTitleEvaluationPurpose = () => {
		const selectedTitleEvaluationPurposeTypeId = this.getSelectedTitleEvaluationPurposeTypeId();
		const { evaluationPurposePopoverOpen } = this.state;

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="evaluation_purpose" />
					:&nbsp;
				</strong>
				<Popover
					placement="bottom"
					trigger="click"
					content={this.renderTitleEvaluationPurposePopover()}
					onVisibleChange={evaluationPurposePopoverOpen =>
						this.setState({ evaluationPurposePopoverOpen })
					}
					visible={evaluationPurposePopoverOpen}>
					<span className="clickable">
						{selectedTitleEvaluationPurposeTypeId ? (
							<Translate
								id={`evalType_${selectedTitleEvaluationPurposeTypeId}`}
							/>
						) : (
							<Translate id="please_select" />
						)}
						<span
							className={this.getPopoverArrowClassName(
								evaluationPurposePopoverOpen,
							)}
						/>
					</span>
				</Popover>
			</p>
		);
	};

	renderTitleCourseAdoptionRequestReason = () => {
		const { department, course, enrollment, requestReason } = this.state;

		return (
			<p className="margin-bottom-5 overflow-none">
				<div id="course-adoption-info-left" className="left">
					<div>
						<strong>
							<Translate id="department" />
						</strong>
						<Input
							value={department}
							onChange={e =>
								this.setState({ department: e.target.value })
							}
						/>
					</div>
					<div>
						<strong>
							<Translate id="course" />
						</strong>
						<Input
							value={course}
							onChange={e =>
								this.setState({ course: e.target.value })
							}
						/>
					</div>
					<div>
						<strong>
							<Translate id="exp_enrollment" />
						</strong>
						<Input
							value={enrollment}
							onChange={e =>
								this.setState({ enrollment: e.target.value })
							}
						/>
					</div>
				</div>
				<div id="course-adoption-info-right" className="left">
					<strong>
						<Translate id="additional_information" />
					</strong>
					<Input.TextArea
						className="full-width margin-top-5"
						rows={5}
						value={requestReason}
						onChange={e =>
							this.setState({ requestReason: e.target.value })
						}
					/>
				</div>
			</p>
		);
	};

	renderTitleRequestReason = () => {
		if (this.isCourseAdoptionSelected()) {
			return this.renderTitleCourseAdoptionRequestReason();
		}

		const { requestReason } = this.state;

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="why_are_you_requesting_this_title" />
				</strong>
				<Input.TextArea
					className="full-width margin-top-5"
					rows={3}
					value={requestReason}
					onChange={e =>
						this.setState({ requestReason: e.target.value })
					}
				/>
			</p>
		);
	};

	renderMissingInfo = text => {
		const accentFontColor = this.getAccentFontColor();

		return (
			<p className="margin-bottom-5">
				<b style={{ color: accentFontColor }}>{text}</b>
			</p>
		);
	};

	renderPhoneNumber = () => {
		const phone = this.getPhoneNumber();

		if (!phone) {
			return this.renderMissingInfo(
				'No phone number entered. Please add a contact phone number.',
			);
		}

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="phone" />
					:&nbsp;
				</strong>
				<span>{phone}</span>
			</p>
		);
	};

	renderShipToAddressPopoverRow = address => (
		<div
			className="address-popover-row flex-container flex-column clickable"
			onClick={() => this.handleShipToAddressClick(address.addressId)}>
			<span>
				<b>{address.description}</b>
			</span>
			<span>{address.fullName}</span>
			<span>{address.streetAddress1}</span>
			<span>
				{address.city},&nbsp;{address.stateProvince}&nbsp;
				{address.postalCode}
			</span>
			<span>{address.country}</span>
		</div>
	);

	renderShipToAddressPopover = () => {
		const addresses = this.getAddresses();

		return (
			<div id="address-popover-content">
				{addresses.map(address =>
					this.renderShipToAddressPopoverRow(address),
				)}
			</div>
		);
	};

	renderShipToAddress = () => {
		const addresses = this.getAddresses();

		if (!addresses || addresses.length === 0) {
			return this.renderMissingInfo(
				'No addresses entered. Please add a address.',
			);
		}

		const { addressPopoverOpen } = this.state;
		const selectedAddress = this.getSelectedAddress();

		return (
			<p className="margin-bottom-5">
				<strong>
					<Translate id="ship_to_address" />
					:&nbsp;
				</strong>
				<Popover
					placement="bottom"
					trigger="click"
					content={this.renderShipToAddressPopover()}
					visible={addressPopoverOpen}
					onVisibleChange={addressPopoverOpen =>
						this.setState({ addressPopoverOpen })
					}>
					<span className="clickable">
						<span>{selectedAddress.description}</span>
						<span
							className={this.getPopoverArrowClassName(
								addressPopoverOpen,
							)}
						/>
					</span>
				</Popover>
			</p>
		);
	};

	renderPrcFields = () => (
		<div>
			{this.renderPhoneNumber()}
			{this.renderShipToAddress()}
		</div>
	);

	renderTitleInfo = () => (
		<div className="flex-container">
			<div>
				{this.renderTitleJacketCover()}
				{this.renderTitleFormats()}
			</div>
			<div className="pad-left-20 full-width">
				{this.renderTitleName()}
				{this.renderTitlePublisher()}
				{this.renderTitleEvaluationPurpose()}
				{this.isPrcSelected() && this.renderPrcFields()}
				{this.renderTitleRequestReason()}
			</div>
		</div>
	);

	renderLibraryUpgradeInfo = () => {
		const isAccountUpgradable = this.isAccountUpgradable();
		const isLibrary = this.isLibrary();

		if (!isAccountUpgradable || !isLibrary) {
			return null;
		}

		return (
			<div id="library-upgrade-info">
				<span>
					<Translate id="librarians" />:
					<br />
					<Translate id="gain_more_recognition_by_publishers_react" />
				</span>
				<br />
				<a
					href={process.env.REACT_APP_LIBRARY_UPGRADE_URL}
					id="library-upgrade-info-link">
					<Translate id="learn_more" />
				</a>
			</div>
		);
	};
}

const connectors = [
	connectToUser,
	connectToUserPreferences,
	connectToRequests,
	connectToAccountOrMembershipRequiredModalContext,
];

export default combineConnectors(connectors)(
	withIsMobile(ReviewCopyRequestModalAnchor),
);
